import { useEffect, useState } from 'react'
import { Badge, Breadcrumb, Button, ButtonGroup, Col, Dropdown, Form, InputGroup, Spinner } from 'react-bootstrap'
import Select from 'react-select'
import moment from 'moment'
import { toast } from 'react-toastify'
import { useLazyQuery, useQuery } from '@apollo/client'
import { GET_ALL_CONTACTS_API } from '../../gql/queries/contactsApiQueries'
import { ModalHealthSherpaSearch } from '../policies/components/ModalHealthSherpaSearch'
import { GET_ALL_ORGANIZATIONS } from '../../gql/queries/organizationsQuery'
import { Tooltip } from 'react-tooltip'
import {
  getDocumentVariant,
  getPaymentVariant,
  INITIAL_CONTACT_FILTERS,
  IMMIGRATION_STATUS_CHOICES,
  SIZE_PAGE_DEFAULT,
} from '../../helpers/consts'
import { IResContactsAPI, MemberPolicySummary, NodeContact, PolicySummary } from '../../types/contactsApi'
import { IHealthSherpaContact } from '../../types/policiesApi'
import { ContactFilters } from '../contacts/contactsTypes'
import { PaginationSection } from '../shared/PaginationSection'
import { PaginationUtils } from '../shared/PaginationUtils'
import { ModalFilters } from '../contacts/components/ModalFilters'
import { AppliedFiltersList } from '../policies/components/AppliedFiltersList'
import { ModalPreview } from '../contacts/components/ModalPreview'
import { GENDER_OPTIONS } from '../../helpers/catalogs'
import { GET_CONTACT_TAGS } from '../../gql/queries/tags'
import { getTagContrastColor } from '../../helpers'
import { BulkTagModal } from '../recipients/components/BulkTagModal'
import { TagManager } from '../recipients/components/TagManager'
import { ModalPreview as PolicyModalPreview } from '../policies/components/ModalPreview'
import { useDisclourse } from '../../hooks/useDisclourse'
import { hasPermission, parseId } from '../../helpers'
import { ALL_PERMISSIONS } from '../../helpers/consts'
import { useGetUser } from '../../store/userStore'
import { useLoadFilterData } from '../contacts/ContactsPage'
import { ModalAddPolice } from '../policies/components/ModalAddPolice'
import { ModalNotes } from '../onePolicy/components/ModalNotes'
import { ModalDuplicatePolicy } from '../onePolicy/components/ModalDuplicatePolicy'
import { BadgeStatePolicy } from '../onePolicy/components/BadgeStatePolicy'
import { getColorByStatus, getTitleByStatus } from '../shared/utils'
import { URL } from '../../gql/client'
import { PhoneProviderActions } from '../../components/telephony/PhoneProviderActions'

type MedicarePolicyInfo = {
  hasPolicy: boolean
  role: 'Holder' | 'Family' | '-'
  policy: PolicySummary | null
  referenceDate: string | null
}

type PolicyStatusLookupEdge = {
  node?: {
    code?: string | null
    category?: string | null
  }
}

type MedicareFilterValue =
  | 'ALL_ELIGIBLE'
  | 'AGE_65_PLUS'
  | 'TURNING_65'
  | 'AGE_64_NOT_APPROACHING'
  | 'HAS_MEDICARE'

const MEDICARE_ALL_FILTER_VALUE: MedicareFilterValue = 'ALL_ELIGIBLE'
const MEDICARE_FILTER_OPTIONS: { value: MedicareFilterValue; label: string }[] = [
  { value: MEDICARE_ALL_FILTER_VALUE, label: 'All' },
  { value: 'AGE_65_PLUS', label: 'Eligible Medicare (65+)' },
  { value: 'TURNING_65', label: 'Eligible <65 (<= 3 months to 65)' },
  { value: 'AGE_64_NOT_APPROACHING', label: 'Age 64 – More than 3 months to 65' },
  { value: 'HAS_MEDICARE', label: 'Has Medicare (Active policy)' },
]

const getMedicareFilterOption = (value?: string) =>
  MEDICARE_FILTER_OPTIONS.find((option) => option.value === value) || MEDICARE_FILTER_OPTIONS[0]

type ImmigrationQuickFilterValue = 'CITIZEN_OR_RESIDENT' | 'CITIZEN' | 'RESIDENT' | 'ANY'

const IMMIGRATION_QUICK_FILTER_OPTIONS: { value: ImmigrationQuickFilterValue; label: string }[] = [
  { value: 'CITIZEN_OR_RESIDENT', label: 'Only Citizen or Resident' },
  { value: 'CITIZEN', label: 'Only Citizen' },
  { value: 'RESIDENT', label: 'Only Resident' },
  { value: 'ANY', label: 'Any' },
]

const IMMIGRATION_QUICK_FILTER_MAP: Record<ImmigrationQuickFilterValue, string[]> = {
  CITIZEN_OR_RESIDENT: ['CITIZEN', 'RESIDENT'],
  CITIZEN: ['CITIZEN'],
  RESIDENT: ['RESIDENT'],
  ANY: [],
}

const getImmigrationQuickFilterValue = (
  status: string | string[] | undefined
): ImmigrationQuickFilterValue => {
  const arr = Array.isArray(status) ? status : status ? [status] : []
  const sorted = [...arr].sort().join(',')
  if (sorted === 'CITIZEN') return 'CITIZEN'
  if (sorted === 'RESIDENT') return 'RESIDENT'
  if (sorted === 'CITIZEN,RESIDENT') return 'CITIZEN_OR_RESIDENT'
  return 'ANY'
}

const getImmigrationQuickFilterOption = (status: string | string[] | undefined) =>
  IMMIGRATION_QUICK_FILTER_OPTIONS.find((option) => option.value === getImmigrationQuickFilterValue(status))

const isMedicarePolicy = (policy?: PolicySummary | null) => {
  const haystack = `${policy?.policyName || ''} ${policy?.type || ''} ${policy?.typePolicy?.typePolicy || ''} ${policy?.carrier?.name || ''}`.toLowerCase()
  return haystack.includes('medicare')
}

const hasMedicarePolicy = (contact: NodeContact) => {
  const policies: Array<PolicySummary | null | undefined> = [
    contact.memberPolicy?.policy,
    ...(contact.members?.edges?.map((edge) => edge.node.policy) || []),
  ]
  return policies.some((policy) => policy && isMedicarePolicy(policy))
}

const getPolicyReferenceDate = (policy?: PolicySummary | null) =>
  policy?.completedDate || policy?.effectiveDate || policy?.created || null

const getPolicyStatusCategory = (statusCode: string | null | undefined, statuses: PolicyStatusLookupEdge[]) => {
  if (!statusCode) return ''
  const match = statuses.find(({ node }) => node?.code?.toUpperCase() === statusCode.toUpperCase())
  return (match?.node?.category || '').toUpperCase()
}

const isActivePolicy = (policy: PolicySummary, statuses: PolicyStatusLookupEdge[]) => {
  const statusCode = (policy.status || '').toUpperCase()
  const category = getPolicyStatusCategory(statusCode, statuses)

  if (category) {
    if (category === 'COMPLETED' || category === 'QUOTE_CONVERTED') return true
    if (category === 'CANCELED' || category === 'QUOTED' || category === 'PENDING') return false
  }

  if (statusCode.includes('CANCEL') || statusCode.includes('QUOTE')) return false
  if (statusCode.includes('ACTIVE') || statusCode.includes('IN_FORCE') || statusCode.includes('COMPLETED')) {
    return true
  }

  return false
}

const getPolicyLineName = (policy?: PolicySummary | null) => {
  if (!policy) return '-'

  const haystack = `${policy.policyName || ''} ${policy.type || ''} ${policy.typePolicy?.typePolicy || ''} ${policy.carrier?.name || ''}`.toLowerCase()

  if (haystack.includes('dental')) return 'Dental'
  if (haystack.includes('vision')) return 'Vision'

  if (
    policy.hasAutoPolicy ||
    policy.hasHomePolicy ||
    haystack.includes('p&c') ||
    haystack.includes('property') ||
    haystack.includes('casualty') ||
    haystack.includes('auto') ||
    haystack.includes('home') ||
    haystack.includes('renters')
  ) {
    return 'P&C'
  }

  if (haystack.includes('aca') || haystack.includes('marketplace') || haystack.includes('obamacare')) {
    return 'ACA'
  }

  if (haystack.includes('health') || haystack.includes('medical')) return 'Health'
  if (haystack.includes('medicare')) return 'Medicare'

  return policy.typePolicy?.typePolicy || policy.type || policy.policyName || '-'
}

const getLatestActivePolicy = (contact: NodeContact, statuses: PolicyStatusLookupEdge[]) => {
  const policies: PolicySummary[] = []
  const seenIds = new Set<string>()

  const addPolicy = (policy?: PolicySummary | null) => {
    if (!policy?.id || seenIds.has(policy.id)) return
    seenIds.add(policy.id)
    policies.push(policy)
  }

  addPolicy(contact.memberPolicy?.policy)
  contact.members?.edges?.forEach(({ node }) => addPolicy(node.policy))

  const activePolicies = policies.filter((policy) => isActivePolicy(policy, statuses))
  if (!activePolicies.length) return null

  activePolicies.sort(
    (a, b) =>
      new Date(getPolicyReferenceDate(b) || '1970-01-01').getTime() -
      new Date(getPolicyReferenceDate(a) || '1970-01-01').getTime()
  )

  return activePolicies[0]
}

const normalizeRole = (role?: string | null) => {
  const value = (role || '').toUpperCase()
  if (value === 'HOLDER') return 'Holder'
  if (value === 'FAMILY') return 'Family'
  return '-'
}

const getMedicarePolicyInfo = (contact: NodeContact, medicareOnly = false): MedicarePolicyInfo => {
  const candidates: { role: 'Holder' | 'Family' | '-'; policy: PolicySummary; date: string | null }[] = []

  const holderPolicy = contact.memberPolicy?.policy
  const holderDate = getPolicyReferenceDate(holderPolicy)
  if (holderPolicy && (!medicareOnly || isMedicarePolicy(holderPolicy))) {
    candidates.push({ role: normalizeRole(contact.memberPolicy?.rol), policy: holderPolicy, date: holderDate })
  }

  const familyPolicies = contact.members?.edges || []
  familyPolicies.forEach(({ node }) => {
    const familyPolicy = node.policy
    const familyDate = getPolicyReferenceDate(familyPolicy)
    if (familyPolicy && (!medicareOnly || isMedicarePolicy(familyPolicy))) {
      candidates.push({ role: normalizeRole(node.rol), policy: familyPolicy, date: familyDate })
    }
  })

  if (candidates.length === 0) {
    return { hasPolicy: false, role: '-', policy: null, referenceDate: null }
  }

  candidates.sort(
    (a, b) => new Date(b.date || '1970-01-01').getTime() - new Date(a.date || '1970-01-01').getTime()
  )
  const latest = candidates[0]

  return {
    hasPolicy: true,
    role: latest.role,
    policy: latest.policy,
    referenceDate: latest.date,
  }
}

const getHealthSherpaSyncMember = (contact: NodeContact, policyId?: string | null): MemberPolicySummary | null => {
  const members = contact.members?.edges?.map(({ node }) => node) || []
  const syncMembers = [contact.memberPolicy, ...members].filter(
    (member): member is MemberPolicySummary => Boolean(member?.id)
  )
  if (syncMembers.length === 0) return null

  if (policyId) {
    const memberForDisplayedPolicy = syncMembers.find((member) => member.policy?.id === policyId)
    if (memberForDisplayedPolicy) return memberForDisplayedPolicy
  }

  if (contact.memberPolicy?.id) return contact.memberPolicy

  const sortedMembers = [...syncMembers].sort(
    (a, b) =>
      new Date(getPolicyReferenceDate(b.policy) || '1970-01-01').getTime() -
      new Date(getPolicyReferenceDate(a.policy) || '1970-01-01').getTime()
  )

  return sortedMembers[0] || null
}

const MEDICARE_ALLOWED_IMMIGRATION_STATUSES = ['CITIZEN', 'RESIDENT'] as const

const isWithinThreeMonthsOf65 = (dateBirth?: string | null) => {
  if (!dateBirth) return false
  const sixtyFifthBirthday = moment(dateBirth).add(65, 'years')
  const monthsRemaining = sixtyFifthBirthday.diff(moment(), 'months', true)
  return monthsRemaining > 0 && monthsRemaining <= 3
}

const isAge65OrMore = (dateBirth?: string | null) => {
  if (!dateBirth) return false
  return moment().diff(moment(dateBirth), 'years') >= 65
}

export const MedicareCenterPage = () => {
  const authUser = useGetUser((s) => s.user)
  const permissions = useGetUser((user) => user?.user?.permissions) ?? []
  const userGroups = useGetUser((s) => s.user?.groups) || []
  const isSuperAdmin = userGroups.includes('SuperAdministrador')
  const canViewContacts = hasPermission(permissions, ALL_PERMISSIONS.VIEW_CONTACT)
  const canExportPolicy = permissions && hasPermission(permissions, ALL_PERMISSIONS.EXPORT_POLICY)

  const [gqlGetContacts, { data: dataAllContacts, loading: loadingContacts, error }] = useLazyQuery<IResContactsAPI>(
    GET_ALL_CONTACTS_API,
    { fetchPolicy: 'no-cache' }
  )

  const { isOpen: isOpenFilters, onOpen: onOpenFilters, onClose: onCloseFilters } = useDisclourse()
  const { isOpen: isOpenPreview, onOpen: onOpenPreview, onClose: onClosePreview } = useDisclourse()
  const { isOpen: isOpenNewPolicy, onOpen: onOpenNewPolicy, onClose: onCloseNewPolicy } = useDisclourse()
  const { isOpen: isOpenNotes, onOpen: onOpenNotes, onClose: onCloseNotes } = useDisclourse()
  const { isOpen: isOpenDuplicate, onOpen: onOpenDuplicate, onClose: onCloseDuplicate } = useDisclourse()
  const { isOpen: isOpenPolicyPreview, onOpen: onOpenPolicyPreview, onClose: onClosePolicyPreview } = useDisclourse()
  const [infoPreview, setInfoPreview] = useState<null | NodeContact>(null)
  const [tagTargetId, setTagTargetId] = useState<string | null>(null)
  const [showTagManager, setShowTagManager] = useState(false)
  const [selectedContactForPolicy, setSelectedContactForPolicy] = useState<null | NodeContact>(null)
  const [selectedPolicyIdForNotes, setSelectedPolicyIdForNotes] = useState<string>('')
  const [selectedPolicyIdForDuplicate, setSelectedPolicyIdForDuplicate] = useState<string>('')
  const [selectedPolicyIdForPreview, setSelectedPolicyIdForPreview] = useState<string>('')
  const [selectedPolicyStatusColor, setSelectedPolicyStatusColor] = useState<string | undefined>(undefined)
  const [hsSearchModalOpen, setHsSearchModalOpen] = useState(false)
  const [hsSearchResults, setHsSearchResults] = useState<IHealthSherpaContact[]>([])
  const [hsAgentEmail, setHsAgentEmail] = useState<string>('')
  const [hsSelectedMember, setHsSelectedMember] = useState<{
    id: string
    firstName: string
    lastName: string
    dateBirth: string
    phone?: string | null
    email?: string | null
    healthsherpaContactId?: string | null
    address1?: string | null
    address2?: string | null
    city?: string | null
    state?: string | null
    zip?: string | null
    gender?: string | null
  } | null>(null)
  const [hsSearchingMemberId, setHsSearchingMemberId] = useState<string | null>(null)

  const handleSearchBeforeSync = (memberPolicy: MemberPolicySummary, contact: NodeContact) => {
    const numericId = String(parseId(memberPolicy.id))
    setHsSelectedMember({
      id: numericId,
      firstName: memberPolicy.firstName || contact.firstName || '',
      lastName: memberPolicy.lastName || contact.lastName || '',
      dateBirth: contact.dateBirth || '',
      phone: memberPolicy.phone || contact.phone || '',
      email: contact.email || '',
      healthsherpaContactId: memberPolicy.healthsherpaContactId || null,
      address1: contact.address1 || '',
      address2: contact.address2 || '',
      city: contact.city || '',
      state: contact.stateEU?.code || contact.stateEU?.name || '',
      zip: contact.zipCode || '',
      gender: contact.gender || '',
    })
    setHsSearchResults([])
    setHsAgentEmail('')
    setHsSearchModalOpen(true)
    setHsSearchingMemberId(null)
  }

  const handleHsSyncSuccess = () => {
    refetchMedicareContacts()
  }

  const [filterCatalogsEnabled, setFilterCatalogsEnabled] = useState(false)
  useEffect(() => {
    const t = setTimeout(() => setFilterCatalogsEnabled(true), 1500)
    return () => clearTimeout(t)
  }, [])

  const {
    dataUsers,
    dataSignatureForms,
    dataCarriers,
    dataPolicyStatuses,
    dataStates,
  } = useLoadFilterData(filterCatalogsEnabled)

  const {
    data: dataOrganizations,
    loading: loadingOrganizations,
    error: errorOrganizations,
  } = useQuery(GET_ALL_ORGANIZATIONS, {
    variables: { first: 100 },
    fetchPolicy: 'no-cache',
    skip: !isSuperAdmin,
  })

  const filterData = {
    users:
      dataUsers?.allUsersList?.map((u) => ({
        ...u,
        fullName: `${u.firstName} ${u.lastName}`,
      })) || [],
    states: dataStates?.allStatesList || [],
    immigrationStatuses: IMMIGRATION_STATUS_CHOICES.filter((option) =>
      MEDICARE_ALLOWED_IMMIGRATION_STATUSES.includes(
        option.value as (typeof MEDICARE_ALLOWED_IMMIGRATION_STATUSES)[number]
      )
    ),
    signatureForms: dataSignatureForms?.allSignatureForms?.edges.map(({ node }) => node) || [],
    carriers: dataCarriers?.allInsuranceOperatorsList || [],
    policyStatuses:
      dataPolicyStatuses?.allPolicyStatuses?.edges.map(({ node }) => ({
        id: node.id,
        status: node.code,
      })) || [],
  }

  const [currentPage, setCurrentPage] = useState(1)
  const [first, setFirst] = useState(SIZE_PAGE_DEFAULT)
  const [loadingExport, setLoadingExport] = useState(false)
  const currentYear = new Date().getFullYear()
  const lastThreeYears = [currentYear - 1, currentYear, currentYear + 1]
  const [selectedYears, setSelectedYears] = useState<number[]>(() => {
    try {
      const item = window.localStorage.getItem('medicare-selected-years')
      return item ? (JSON.parse(item) as number[]) : []
    } catch {
      return []
    }
  })
  const [formData, setFormData] = useState<ContactFilters>({
    ...INITIAL_CONTACT_FILTERS,
    ageFrom: '',
    immigrationStatus: [...MEDICARE_ALLOWED_IMMIGRATION_STATUSES],
  })
  const totalContacts = dataAllContacts?.allContacts?.totalCount ?? 0
  const dataArr = dataAllContacts?.allContacts?.edges || []
  const totalPages = Math.max(1, Math.ceil(totalContacts / first))
  const { start, end } = PaginationUtils.getPaginationRange(currentPage, first, totalContacts)

  const cleanVariables = (vars: any) =>
    Object.fromEntries(Object.entries(vars).filter(([_, v]) => v !== '' && v !== null && v !== undefined))

  useEffect(() => {
    try {
      window.localStorage.setItem('medicare-selected-years', JSON.stringify(selectedYears))
    } catch {
      // ignore localStorage write errors
    }
  }, [selectedYears])

  const buildMedicareServerFilters = (
    baseFilters: ContactFilters,
    medicareUnifiedFilter = baseFilters.medicareUnifiedFilter
  ) => {
    const filtersToSend: any = { ...baseFilters, medicareUnifiedFilter }
    const unifiedFilter = medicareUnifiedFilter || MEDICARE_ALL_FILTER_VALUE
    const isAllFilter = unifiedFilter === MEDICARE_ALL_FILTER_VALUE

    if (isAllFilter) {
      filtersToSend.ageFrom = ''
      filtersToSend.ageTo = ''
      filtersToSend.turning65In3Months = undefined
      filtersToSend.age64NotApproaching65 = undefined
      filtersToSend.policyType = ''
      filtersToSend.policyStatus = ''
      filtersToSend.withoutPolicy = undefined
      filtersToSend.medicareUnifiedFilter = MEDICARE_ALL_FILTER_VALUE
    } else if (unifiedFilter === 'TURNING_65') {
      filtersToSend.ageFrom = ''
      filtersToSend.ageTo = ''
      filtersToSend.turning65In3Months = true
      filtersToSend.age64NotApproaching65 = undefined
      filtersToSend.policyType = ''
      filtersToSend.policyStatus = ''
      filtersToSend.withoutPolicy = undefined
    } else if (unifiedFilter === 'AGE_65_PLUS') {
      filtersToSend.ageFrom = '65'
      filtersToSend.ageTo = ''
      filtersToSend.turning65In3Months = undefined
      filtersToSend.age64NotApproaching65 = undefined
      filtersToSend.policyType = ''
      filtersToSend.policyStatus = ''
      filtersToSend.withoutPolicy = undefined
    } else if (unifiedFilter === 'AGE_64_NOT_APPROACHING') {
      filtersToSend.ageFrom = ''
      filtersToSend.ageTo = ''
      filtersToSend.turning65In3Months = undefined
      filtersToSend.age64NotApproaching65 = true
      filtersToSend.policyType = ''
      filtersToSend.policyStatus = ''
      filtersToSend.withoutPolicy = undefined
    } else if (unifiedFilter === 'HAS_MEDICARE') {
      filtersToSend.ageFrom = ''
      filtersToSend.ageTo = ''
      filtersToSend.turning65In3Months = undefined
      filtersToSend.age64NotApproaching65 = undefined
      filtersToSend.policyType = 'medicare'
      filtersToSend.policyStatus = ''
      filtersToSend.withoutPolicy = undefined
    } else {
      filtersToSend.ageFrom = ''
      filtersToSend.ageTo = ''
      filtersToSend.turning65In3Months = undefined
      filtersToSend.age64NotApproaching65 = undefined
      filtersToSend.policyType = ''
      filtersToSend.policyStatus = ''
      filtersToSend.withoutPolicy = undefined
    }

    if (filtersToSend.isPolicyHolder === false) {
      delete filtersToSend.isPolicyHolder
    }

    // "Any" (immigrationStatus === []) significa no filtrar por este campo en absoluto
    const selectedImmigrationStatuses = Array.isArray(baseFilters.immigrationStatus)
      ? baseFilters.immigrationStatus
      : baseFilters.immigrationStatus
        ? [baseFilters.immigrationStatus]
        : []
    if (selectedImmigrationStatuses.length) {
      filtersToSend.immigrationStatus = selectedImmigrationStatuses.join(',')
    } else {
      delete filtersToSend.immigrationStatus
    }

    if (!isAllFilter) {
      delete filtersToSend.medicareUnifiedFilter
    }

    // hasSignedEntry queda "pegado" en formData si signatureStatus se vuelve a Select...
    if (!filtersToSend.signatureStatus) {
      delete filtersToSend.hasSignedEntry
    }

    return filtersToSend
  }

  const buildContactQueryVariables = (
    filtersToSend: any,
    baseFilters: ContactFilters,
    pageSize: number,
    offset: number
  ) =>
    cleanVariables({
      ...filtersToSend,
      effectiveYear: selectedYears.length > 0 ? selectedYears.join(',') : null,
      organization: isSuperAdmin && baseFilters.organizationId ? parseId(baseFilters.organizationId) : null,
      first: pageSize,
      offset,
    })

  const runSearch = (baseFilters: ContactFilters, pageOverride?: number) => {
    setFormData(baseFilters)

    const filtersToSend = buildMedicareServerFilters(baseFilters)
    const pageToUse = pageOverride ?? currentPage
    gqlGetContacts({
      variables: buildContactQueryVariables(filtersToSend, baseFilters, first, (pageToUse - 1) * first),
    })
  }

  const {data: tagsData} = useQuery(GET_CONTACT_TAGS, {skip: !filterCatalogsEnabled})
  const tagOptions = (tagsData?.contactTags || []).map((t: any) => ({value: t.id, label: t.name}))

  const monthOptions = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ].map((month, index) => ({value: String(index + 1), label: month}))

  const contactUserOptions = filterData.users.map((u: any) => ({value: u.id, label: `${u.fullName} - ${u.email}`}))
  const languageOptions = [
    {value: 'Spanish', label: 'Spanish'},
    {value: 'English', label: 'English'},
  ]
  const genderOptions = GENDER_OPTIONS.map((s) => ({value: s.value, label: s.label}))
  const contactStateOptions = filterData.states.map((s: any) => ({value: s.code, label: s.name}))
  const signatureStatusOptions = [
    {value: 'SIGNED', label: 'Signed'},
    {value: 'NOT_SIGNED', label: 'Did not sign'},
  ]
  const contactSignatureFormOptions = filterData.signatureForms.map((f: any) => ({value: f.id, label: f.name}))
  const contactCarrierOptions = filterData.carriers.map((c: any) => ({value: c.id, label: c.name}))
  const contactPolicyStatusOptions = filterData.policyStatuses.map((s: any) => ({value: s.status, label: s.status}))

  const formatDate = (dateStr: string) => {
    if (!dateStr) return ''
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    return date.toLocaleDateString('en-US', {year: 'numeric', month: 'short', day: 'numeric'})
  }

  const contactFiltersConfig = [
    {key: 'user', label: 'User', options: contactUserOptions},
    {key: 'preferredLanguage', label: 'Preferred Language', options: languageOptions},
    {key: 'birthMonth', label: 'Date of Birth Month', options: monthOptions},
    {key: 'ageFrom', label: 'Age From'},
    {key: 'ageTo', label: 'Age To'},
    {key: 'gender', label: 'Gender', options: genderOptions},
    {key: 'state', label: 'State', options: contactStateOptions},
    {key: 'zipCode', label: 'Zip Code'},
    {
      key: 'tags',
      label: 'Tags',
      transform: (value: string) => {
        const ids = value.split(',').filter(Boolean)
        const labels = ids
          .map((id) => tagOptions.find((o: any) => String(o.value) === id)?.label)
          .filter(Boolean)
        return labels.length ? labels.join(', ') : value
      },
    },
    {key: 'signatureStatus', label: 'Signature Form Status', options: signatureStatusOptions},
    {key: 'signatureForm', label: 'Signature Form', options: contactSignatureFormOptions},
    {key: 'carrier', label: 'Carrier', options: contactCarrierOptions},
    {key: 'policyStatus', label: 'Policy Status', options: contactPolicyStatusOptions},
  ]

  const contactDateRangeFilters = [
    {keyFrom: 'createdAfter', keyTo: 'createdBefore', label: 'Contact Created'},
    {keyFrom: 'effectiveDateAfter', keyTo: 'effectiveDateBefore', label: 'Effective Date'},
    {keyFrom: 'policyCreatedFrom', keyTo: 'policyCreatedTo', label: 'Policy Created Date'},
    {keyFrom: 'submissionDateFrom', keyTo: 'submissionDateTo', label: 'Submission Date'},
  ]

  const clearAdvancedFilters = () => {
    const next: ContactFilters = {
      ...INITIAL_CONTACT_FILTERS,
      ageFrom: '',
      medicareUnifiedFilter: formData.medicareUnifiedFilter,
      immigrationStatus: [...MEDICARE_ALLOWED_IMMIGRATION_STATUSES],
    }
    setCurrentPage(1)
    runSearch(next, 1)
  }

  const handleRemoveFilter = (filterKey: string | string[]) => {
    const next: any = {...formData}
    const keysToReset = Array.isArray(filterKey) ? filterKey : [filterKey]

    keysToReset.forEach((key) => {
      if (Array.isArray(next[key])) next[key] = []
      else if (typeof next[key] === 'boolean') next[key] = undefined
      else next[key] = ''
    })

    setCurrentPage(1)
    runSearch(next, 1)
  }

  const handleExport = async () => {
    if (!authUser?.token) return
    setLoadingExport(true)

    const toBoolParam = (value: unknown) => {
      if (value === true) return 'true'
      if (value === false) return 'false'
      return null
    }

    const filtersToSend: any = buildMedicareServerFilters(formData)
    const paramsMap: Record<string, string | null | undefined> = {
      name: filtersToSend.name || null,
      status: filtersToSend.status || null,
      policy_status: filtersToSend.policyStatus || null,
      policy_type: filtersToSend.policyType || null,
      state: filtersToSend.state || null,
      carrier: filtersToSend.carrier || null,
      created_after: filtersToSend.createdAfter || null,
      created_before: filtersToSend.createdBefore || null,
      birth_month: filtersToSend.birthMonth || null,
      age_from: filtersToSend.ageFrom || null,
      age_to: filtersToSend.ageTo || null,
      medicare_unified_filter: filtersToSend.medicareUnifiedFilter || null,
      turning_65_in_3_months: toBoolParam(filtersToSend.turning65In3Months),
      age_64_not_approaching_65: toBoolParam(filtersToSend.age64NotApproaching65),
      gender: filtersToSend.gender || null,
      preferred_language: filtersToSend.preferredLanguage || null,
      immigration_status: filtersToSend.immigrationStatus || null,
      zip_code: filtersToSend.zipCode || null,
      is_policy_holder: toBoolParam(filtersToSend.isPolicyHolder),
      without_policy: toBoolParam(filtersToSend.withoutPolicy),
      without_lead: toBoolParam(filtersToSend.withoutLead),
      my_contacts: toBoolParam(filtersToSend.myContacts),
      is_important: toBoolParam(filtersToSend.isImportant),
      user: filtersToSend.user || null,
      assigned_to: filtersToSend.assignedTo || null,
      signature_form: filtersToSend.signatureForm || null,
      has_signed_entry: toBoolParam(filtersToSend.hasSignedEntry),
      effective_date_after: filtersToSend.effectiveDateAfter || null,
      effective_date_before: filtersToSend.effectiveDateBefore || null,
      policy_created_from: filtersToSend.policyCreatedFrom || null,
      policy_created_to: filtersToSend.policyCreatedTo || null,
      submission_date_from: filtersToSend.submissionDateFrom || null,
      submission_date_to: filtersToSend.submissionDateTo || null,
      completed_date_from: filtersToSend.completedDateFrom || null,
      completed_date_to: filtersToSend.completedDateTo || null,
      lead: filtersToSend.lead || null,
      is_recipient: toBoolParam(filtersToSend.isRecipient),
      segment: filtersToSend.segment || null,
      effective_year: selectedYears.length > 0 ? selectedYears.join(',') : null,
      organization: isSuperAdmin && formData.organizationId ? String(parseId(formData.organizationId)) : null,
    }

    const params = new URLSearchParams()
    Object.entries(paramsMap).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value)
      }
    })

    try {
      const res = await fetch(`${URL}/export-medicare-center-excel?${params.toString()}`, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${authUser.token}`,
        },
      })
      if (!res.ok) throw new Error('Error exporting Medicare data')

      const blob = await res.blob()
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = `medicare-center-${moment().format('YYYY-MM-DD')}.xlsx`
      document.body.appendChild(link)
      link.click()
      window.URL.revokeObjectURL(downloadUrl)
      link.remove()
    } catch (err) {
      toast.error(`ERROR: ${(err as { message: string }).message}`)
    } finally {
      setLoadingExport(false)
    }
  }

  const refetchMedicareContacts = () => {
    runSearch(formData)
  }

  const handleFilterApply = (filters: ContactFilters) => {
    const transformedFilters = { ...filters }

    if (filters.signatureStatus === 'SIGNED') {
      ; (transformedFilters as any).hasSignedEntry = true
    } else if (filters.signatureStatus === 'NOT_SIGNED') {
      ; (transformedFilters as any).hasSignedEntry = false
    } else {
      ; (transformedFilters as any).hasSignedEntry = undefined
    }
    delete (transformedFilters as any).signatureStatus

    setCurrentPage(1)
    runSearch({ ...formData, ...transformedFilters }, 1)
  }

  const handleSelectChange = (field: string, selected: any) => {
    let value = selected?.value
    if (value === 'true') value = true
    else if (value === 'false') value = false
    else if (value === '') value = undefined

    const nextFilters = { ...formData, [field]: value }
    setCurrentPage(1)
    runSearch(nextFilters, 1)
  }

  const handleImmigrationQuickFilterChange = (selected: any) => {
    const value: ImmigrationQuickFilterValue = selected?.value || 'CITIZEN_OR_RESIDENT'
    const nextFilters = { ...formData, immigrationStatus: IMMIGRATION_QUICK_FILTER_MAP[value] }
    setCurrentPage(1)
    runSearch(nextFilters, 1)
  }

  useEffect(() => {
    runSearch(formData)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, first, selectedYears])

  useEffect(() => {
    setCurrentPage(1)
  }, [formData.medicareUnifiedFilter])

  useEffect(() => {
    if (error) toast.error(`Error: ${error.message}`)
  }, [error])

  useEffect(() => {
    if (errorOrganizations) toast.error(`Error: ${errorOrganizations.message}`)
  }, [errorOrganizations])

  if (!canViewContacts) {
    return (
      <div className='policies-page'>
        <header className='header'>
          <Breadcrumb>
            <Breadcrumb.Item className='fs-3'>Medicare Center</Breadcrumb.Item>
          </Breadcrumb>
        </header>
        <div className='d-flex flex-center' style={{ height: '40vh' }}>
          <h4>You don't have permission to view Medicare Center.</h4>
        </div>
      </div>
    )
  }

  return (
    <div className='policies-page'>
      <header className='header'>
        <Breadcrumb>
          <Breadcrumb.Item className='fs-3'>Medicare Center</Breadcrumb.Item>
        </Breadcrumb>
        <div className='d-flex align-items-center gap-2'>
          <Button variant='outline-secondary' size='sm' onClick={() => setShowTagManager(true)}>
            <i className='bi bi-tags me-2'></i>
            Manage Tags
          </Button>
          {canExportPolicy && (
            <Button
              disabled={loadingExport}
              className='btn-sm btn-outline btn-export'
              onClick={handleExport}
            >
              {loadingExport ? (
                <>
                  <Spinner animation='border' size='sm' />{' '}
                </>
              ) : (
                <i className='bi bi-download'></i>
              )}
              Export
            </Button>
          )}
          <Button
            size='sm'
            onClick={() => {
              setSelectedContactForPolicy(null)
              onOpenNewPolicy()
            }}
          >
            <i className='bi bi-plus fs-3' /> New Policy
          </Button>
        </div>
      </header>

      <div className='policies-container'>
        <div className='title-header d-flex align-items-center justify-content-between'>
          <h3>Medicare Contacts</h3>
          <div className='filter-by-years'>
            {dataArr.length > 0 && <span className='fw-bold me-4'>Effective year:</span>}
            {lastThreeYears.map((year) => (
              <Form.Check
                key={year}
                inline
                label={year}
                checked={selectedYears.includes(year)}
                onChange={() => {
                  setCurrentPage(1)
                  setSelectedYears((prev) =>
                    prev.includes(year) ? prev.filter((y) => y !== year) : [...prev, year]
                  )
                }}
              />
            ))}
          </div>
        </div>

        <div className='my-2'>
          <InputGroup className='d-flex gap-2'>
            <Form.Control
              placeholder='Search by name'
              onChange={(e) => setFormData((prev) => ({ ...prev, name: e.target.value }))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  runSearch(formData)
                }
              }}
              value={formData.name}
            />
            {isSuperAdmin && (
              <Form.Select
                value={formData.organizationId}
                onChange={(e) => {
                  const value = e.target.value
                  setFormData((prev) => ({ ...prev, organizationId: value }))
                  setCurrentPage(1)
                }}
                disabled={loadingOrganizations}
              >
                {loadingOrganizations ? (
                  <option value=''>Loading...</option>
                ) : (
                  <option value=''>All Organizations</option>
                )}
                {dataOrganizations?.allOrganizations?.edges?.map(({ node }: any) => (
                  <option key={node.id} value={parseId(node.id)}>
                    {node.name}
                  </option>
                ))}
              </Form.Select>
            )}
            <Button onClick={() => runSearch(formData)} variant='outline-success' style={{ border: '1px solid #dbdfe9' }}>
              Search
            </Button>
            <Button variant='outline-primary' style={{ border: '1px solid #dbdfe9' }} onClick={onOpenFilters}>
              Filter
            </Button>
          </InputGroup>
          <div className='d-flex align-items-center gap-4 flex-wrap mt-3'>
            <Form.Check
              type='checkbox'
              id='my-contacts-medicare'
              checked={formData.myContacts ?? false}
              onChange={(e) => setFormData((prev) => ({ ...prev, myContacts: e.target.checked }))}
              label='Show my contacts only'
            />
            <Form.Label className='mb-0'>Medicare Filter</Form.Label>
            <Select
              menuPortalTarget={document.body}
              styles={{
                container: (base) => ({ ...base, flex: '0 0 320px', width: '320px', maxWidth: '100%' }),
                menu: (base) => ({ ...base, width: '320px' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              value={getMedicareFilterOption(formData.medicareUnifiedFilter)}
              options={MEDICARE_FILTER_OPTIONS}
              onChange={(selected) => handleSelectChange('medicareUnifiedFilter', selected)}
            />
            <Form.Label className='mb-0'>Immigration Status</Form.Label>
            <Select
              menuPortalTarget={document.body}
              styles={{
                container: (base) => ({ ...base, flex: '0 0 260px', width: '260px', maxWidth: '100%' }),
                menu: (base) => ({ ...base, width: '260px' }),
                menuPortal: (base) => ({ ...base, zIndex: 9999 }),
              }}
              value={getImmigrationQuickFilterOption(formData.immigrationStatus)}
              options={IMMIGRATION_QUICK_FILTER_OPTIONS}
              onChange={handleImmigrationQuickFilterChange}
            />
          </div>
          <AppliedFiltersList
            filters={formData}
            config={contactFiltersConfig}
            dateRangeFilters={contactDateRangeFilters}
            formatDate={formatDate}
            clearFilters={clearAdvancedFilters}
            onRemoveFilter={handleRemoveFilter}
          />
        </div>

        <div className='results-header'>
          <PaginationSection
            first={first}
            setFirst={(n) => {
              setFirst(n)
              setCurrentPage(1)
            }}
            currentPage={currentPage}
            totalPages={totalPages}
            start={start}
            end={end}
            totalPolicies={totalContacts}
            updatePage={setCurrentPage}
            PaginationUtils={PaginationUtils}
          />
          <div className='col-md-3'>
            <div className='results-filter'>
              <Col lg={12} md={12} sm={12} xs={12}></Col>
            </div>
          </div>
        </div>

        <div className='table-responsive table table-striped table-hover mt-3'>
          <table
            className='table-scrollable table-scroll table table-striped table-hover table-sm'
            style={{ whiteSpace: 'nowrap', fontSize: '0.9rem' }}
          >
            <thead>
              <tr role='row'>
                <th className='fw-bold'>Client</th>
                <th className='fw-bold'>Birthday / Age</th>
                <th className='fw-bold'>Immigration Status</th>
                <th className='fw-bold'>Member Policy</th>
                <th className='fw-bold'>Last Policy</th>
                <th className='fw-bold'>Status</th>
                <th className='fw-bold'>Tags</th>
                <th className='fw-bold'>Actions</th>
              </tr>
            </thead>
            {loadingContacts ? (
              <tbody>
                {Array.from({ length: 10 }).map((_, i) => (
                  <tr key={i} className='placeholder-glow'>
                    <td>
                      <span className='placeholder rounded' style={{ width: 150, display: 'inline-block' }} />
                      <br />
                      <span className='placeholder rounded mt-1' style={{ width: 110, display: 'inline-block' }} />
                      <br />
                      <span className='placeholder rounded mt-1' style={{ width: 90, display: 'inline-block' }} />
                    </td>
                    <td>
                      <span className='placeholder rounded' style={{ width: 90, display: 'inline-block' }} />
                      <br />
                      <span className='placeholder rounded mt-1' style={{ width: 60, display: 'inline-block' }} />
                    </td>
                    <td>
                      <span className='placeholder rounded' style={{ width: 90, display: 'inline-block' }} />
                    </td>
                    <td>
                      <span className='placeholder rounded' style={{ width: 120, display: 'inline-block' }} />
                      <br />
                      <span className='placeholder rounded mt-1' style={{ width: 80, display: 'inline-block' }} />
                    </td>
                    <td>
                      <span className='placeholder rounded' style={{ width: 110, display: 'inline-block' }} />
                      <br />
                      <span className='placeholder rounded mt-1' style={{ width: 130, display: 'inline-block' }} />
                    </td>
                    <td>
                      <span className='placeholder rounded-pill' style={{ width: 80, height: 24, display: 'inline-block' }} />
                    </td>
                    <td>
                      <span className='placeholder rounded-pill' style={{ width: 60, height: 22, display: 'inline-block', marginRight: 4 }} />
                      <span className='placeholder rounded-pill' style={{ width: 50, height: 22, display: 'inline-block' }} />
                    </td>
                    <td>
                      <span className='placeholder rounded' style={{ width: 60, height: 28, display: 'inline-block' }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            ) : (
              <tbody>
                {dataArr.length === 0 && (
                  <tr>
                    <td colSpan={8}>
                      <div className='d-flex flex-center' style={{ height: '30vh' }}>
                        There are no Medicare contacts currently added
                      </div>
                    </td>
                  </tr>
                )}
                {dataArr.map(({ node }) => {
                  const policyInfo = getMedicarePolicyInfo(
                    node,
                    formData.medicareUnifiedFilter === 'HAS_MEDICARE'
                  )
                  const hsSyncMember = getHealthSherpaSyncMember(node, policyInfo.policy?.id)
                  const latestActivePolicy = getLatestActivePolicy(
                    node,
                    dataPolicyStatuses?.allPolicyStatuses?.edges ?? []
                  )
                  const latestActivePolicyLine = getPolicyLineName(latestActivePolicy)
                  const contactHasMedicarePolicy = hasMedicarePolicy(node)
                  const isThreeMonthsFrom65 = isWithinThreeMonthsOf65(node.dateBirth)
                  const isAlready65NoMedicare = isAge65OrMore(node.dateBirth) && !contactHasMedicarePolicy
                  const carrierName = policyInfo.policy?.carrier?.name || '-'
                  const policyName = policyInfo.policy?.policyName || '-'
                  const lastPolicyPrimaryLine =
                    policyInfo.role === 'Holder' ? carrierName : policyName
                  const policyStatusLabel = policyInfo.policy?.status
                    ? getTitleByStatus(
                      dataPolicyStatuses?.allPolicyStatuses?.edges ?? [],
                      policyInfo.policy.status
                    )
                    : ''
                  const policyStatusColor = policyInfo.policy?.status
                    ? getColorByStatus(
                      dataPolicyStatuses?.allPolicyStatuses?.edges ?? [],
                      policyInfo.policy.status
                    )
                    : 'secondary'
                  const documentStatus = policyInfo.hasPolicy
                    ? policyInfo.policy?.stateDocs || 'PENDING'
                    : '-'
                  const paymentStatus = policyInfo.hasPolicy
                    ? policyInfo.policy?.paymentStatus || 'PENDING'
                    : '-'
                  const membersCount = node.members?.edges?.length || 0
                  const policyMembersCount = policyInfo.hasPolicy ? membersCount : 0
                  const showMedicareIcon = contactHasMedicarePolicy

                  return (
                    <tr key={node.id}>
                      <td>
                        <div style={{ maxWidth: '280px', minWidth: '200px' }}>
                          <span
                            className='text-primary text-link fw-bold'
                            style={{
                              cursor: 'pointer',
                              display: 'block',
                              wordBreak: 'break-word',
                              lineHeight: '1.3',
                              whiteSpace: 'normal',
                            }}
                            onClick={() => {
                              setInfoPreview(node)
                              onOpenPreview()
                            }}
                          >
                            {node.fullName}
                          </span>
                          <div className='m-0 p-0' style={{ fontSize: '0.95rem' }}>
                            <PhoneProviderActions phone={node.phone} />
                          </div>
                          <p className='m-0 p-0' style={{ fontSize: '0.95rem' }}>
                            {(node.stateEU?.code || node.stateEU?.name || '-') + ' ' + (node.zipCode || '')}
                          </p>
                          {hsSyncMember && (
                            hsSyncMember.healthsherpaContactId ? (
                              <Badge
                                title='Syncronized with Healthsherpa'
                                bg='success' className='sync-hs-button badge-sm mt-1' style={{ width: 'fit-content' }}>
                                <i className='bi bi-check-circle me-1' />
                                HealthSherpa
                              </Badge>
                            ) : (
                              <Button
                                title='Syncronize Healthsherpa'
                                size='sm'
                                variant='outline-warning'
                                className='mt-1 sync-hs-button'
                                style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                disabled={hsSearchingMemberId === hsSyncMember.id}
                                onClick={() => handleSearchBeforeSync(hsSyncMember, node)}
                              >
                                {hsSearchingMemberId === hsSyncMember.id ? (
                                  <Spinner animation='border' size='sm' />
                                ) : (
                                  'Sync HS'
                                )}
                              </Button>
                            )
                          )}
                        </div>
                      </td>
                      <td>
                        <div style={{ maxWidth: '180px', minWidth: '140px', lineHeight: '1.3' }}>
                          <span className='fw-semibold' style={{ display: 'block' }}>
                            {node.dateBirth ? moment(node.dateBirth).format('MMM DD, YYYY') : '-'}
                          </span>
                          <span style={{ fontSize: '0.95rem', color: '#000', display: 'block' }}>
                            {node.dateBirth ? `${moment().diff(moment(node.dateBirth), 'years')} yrs` : '-'}
                          </span>
                          <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                            {isAlready65NoMedicare && (
                              <Badge
                                bg='danger'
                                className='badge-sm'
                                style={{ width: 'fit-content', color: '#fff' }}
                              >
                                65+ without Medicare
                              </Badge>
                            )}
                            {isThreeMonthsFrom65 && (
                              <Badge
                                bg='warning'
                                text='dark'
                                className='badge-sm'
                                style={{ width: 'fit-content' }}
                              >
                                65 in ≤ 3 months
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className='fw-semibold'>
                          {node.immigrationStatus
                            ? node.immigrationStatus.charAt(0) + node.immigrationStatus.slice(1).toLowerCase()
                            : '-'}
                        </span>
                      </td>
                      <td>
                        <div className='d-flex flex-column'>
                          <span className='fw-semibold'>{policyInfo.hasPolicy ? policyInfo.role : ''}</span>
                        </div>
                      </td>
                      <td>
                        <div
                          style={{
                            maxWidth: '220px',
                            minWidth: '180px',
                            wordBreak: 'break-word',
                            whiteSpace: 'normal',
                            lineHeight: '1.3',
                          }}
                        >
                          <span
                            className='text-primary text-link fw-bold'
                            style={{ cursor: policyInfo.policy ? 'pointer' : 'default', display: 'block' }}
                            data-tooltip-id={`policy-tooltip-${parseId(node.id)}`}
                            onClick={() => {
                              if (!policyInfo.policy?.id) return
                              setSelectedPolicyIdForPreview(String(parseId(policyInfo.policy.id)))
                              setSelectedPolicyStatusColor(policyStatusColor)
                              onOpenPolicyPreview()
                            }}
                          >
                            {lastPolicyPrimaryLine}
                          </span>
                          {policyInfo.role !== 'Holder' && (
                            <span style={{ fontSize: '0.95rem', color: '#000', display: 'block' }}>
                              {carrierName}
                            </span>
                          )}
                          {policyInfo.policy && (
                            <Tooltip
                              id={`policy-tooltip-${parseId(node.id)}`}
                              place='top'
                              style={{
                                zIndex: 99999,
                                backgroundColor: 'white',
                                color: 'black',
                                borderRadius: '12px',
                                padding: '14px 16px',
                                boxShadow:
                                  '0 8px 24px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12)',
                              }}
                            >
                              <div style={{ minWidth: '240px' }}>
                                <div
                                  style={{
                                    fontWeight: 700,
                                    textAlign: 'center',
                                    marginBottom: '8px',
                                  }}
                                >
                                  More information:
                                </div>
                                <div style={{ display: 'grid', gap: '6px' }}>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Product:</span>
                                    <span>
                                      {policyInfo.policy.type ||
                                        policyInfo.policy.typePolicy?.typePolicy ||
                                        '-'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Plan:</span>
                                    <span>
                                      {policyInfo.policy.policyName ||
                                        policyInfo.policy.type ||
                                        '-'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Members:</span>
                                    <span>{policyMembersCount}</span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Carrier:</span>
                                    <span>{policyInfo.policy.carrier?.name || '-'}</span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Status:</span>
                                    <span>{policyStatusLabel || policyInfo.policy.status || '-'}</span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Documents:</span>
                                    <span>{documentStatus}</span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Payment:</span>
                                    <span>{paymentStatus}</span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Effective:</span>
                                    <span>
                                      {policyInfo.policy.effectiveDate
                                        ? moment(policyInfo.policy.effectiveDate).format('MMM DD, YYYY')
                                        : '-'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Submitted:</span>
                                    <span>
                                      {policyInfo.policy.created
                                        ? moment(policyInfo.policy.created).format('MMM DD, YYYY')
                                        : '-'}
                                    </span>
                                  </div>
                                  <div style={{ display: 'grid', gridTemplateColumns: '90px 1fr' }}>
                                    <span className='text-muted'>Agent:</span>
                                    <span>
                                      {policyInfo.policy.assignedTo
                                        ? `${policyInfo.policy.assignedTo.firstName || ''} ${policyInfo.policy.assignedTo.lastName || ''
                                          }`.trim() || '-'
                                        : '-'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </Tooltip>
                          )}
                          <div className='d-flex align-items-center gap-2'>
                            <span style={{ color: '#000', display: 'block' }}>
                              Effective:{' '}
                              {policyInfo.policy?.effectiveDate
                                ? moment(policyInfo.policy.effectiveDate).format('MMM DD, YYYY')
                                : '-'}
                            </span>
                          </div>
                          <div className='d-flex align-items-center gap-2'>
                            <span style={{ color: '#000', display: 'block' }}>
                              Active policy line: {latestActivePolicy ? latestActivePolicyLine : '-'}
                            </span>
                          </div>
                          <div className='d-flex align-items-center gap-2'>
                            {policyMembersCount > 0 && (
                              <Badge
                                bg='dark'
                                className='badge-sm d-inline-flex align-items-center gap-1'
                                style={{ width: 'fit-content', alignSelf: 'flex-start' }}
                              >
                                <i className='bi bi-file-earmark-text-fill' />
                                <span style={{ fontSize: '0.85rem', lineHeight: 1, color: '#fff' }}>
                                  {policyMembersCount}
                                </span>
                              </Badge>
                            )}
                            {showMedicareIcon && (
                              <Badge
                                bg='info'
                                className='badge-sm d-inline-flex align-items-center gap-1'
                                style={{ width: 'fit-content', alignSelf: 'flex-start', color: '#fff' }}
                              >
                                <i className='bi bi-heart-pulse-fill' />
                                Medicare
                              </Badge>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <div className='d-flex flex-column gap-1'>
                          {policyInfo.policy?.status ? (
                            <div style={{ width: 'fit-content', alignSelf: 'flex-start' }}>
                              <BadgeStatePolicy status={policyStatusLabel} color={policyStatusColor} />
                            </div>
                          ) : (
                            <Badge bg='secondary' className='badge-sm' style={{ width: 'fit-content' }}>
                              No policy
                            </Badge>
                          )}
                          <span>
                            Documents:{' '}
                            <Badge
                              className='badge-sm'
                              bg={getDocumentVariant(documentStatus)}
                              style={{ width: 'fit-content' }}
                            >
                              {documentStatus}
                            </Badge>
                          </span>
                          <span>
                            Payment:{' '}
                            <Badge
                              className='badge-sm'
                              bg={getPaymentVariant(paymentStatus)}
                              text={getPaymentVariant(paymentStatus) === 'danger' ? 'white' : undefined}
                              style={{ width: 'fit-content' }}
                            >
                              {paymentStatus}
                            </Badge>
                          </span>
                        </div>
                      </td>
                      <td>
                        {node.tagAssignments && node.tagAssignments.length > 0 ? (
                          <div className='d-flex flex-wrap gap-1'>
                            {node.tagAssignments.map((assignment: any) => (
                              <span
                                key={assignment.tag.id}
                                className='badge rounded-pill'
                                style={{
                                  backgroundColor: assignment.tag.color,
                                  color: getTagContrastColor(assignment.tag.color),
                                  fontSize: '0.7rem',
                                }}
                              >
                                {assignment.tag.name}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span className='text-muted small'>—</span>
                        )}
                      </td>
                      <td>
                        <Dropdown as={ButtonGroup}>
                          <Button
                            variant='outline-primary'
                            size='sm'
                            style={{ border: '1px solid #3e97ff' }}
                            onClick={() => {
                              setInfoPreview(node)
                              onOpenPreview()
                            }}
                          >
                            Preview
                          </Button>
                          <Dropdown.Toggle
                            split
                            variant='outline-primary'
                            size='sm'
                            id={`dropdown-medicare-${parseId(node.id)}`}
                            style={{ border: '1px solid #3e97ff' }}
                          />
                          <Dropdown.Menu className='dropdown-menu-policy'>
                            <Dropdown.Item onClick={() => setTagTargetId(node.id)}>
                              <i className='bi bi-tags' /> Manage Tags
                            </Dropdown.Item>
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={() => {
                                setSelectedContactForPolicy(node)
                                onOpenNewPolicy()
                              }}
                            >
                              <i className='bi bi-plus-circle' /> New Policy
                            </Dropdown.Item>
                            {policyInfo.policy?.id && (
                              <Dropdown.Item
                                onClick={() => {
                                  setSelectedPolicyIdForNotes(String(parseId(policyInfo.policy!.id)))
                                  onOpenNotes()
                                }}
                              >
                                <i className='bi bi-pencil-square' /> Add Note
                              </Dropdown.Item>
                            )}
                            {policyInfo.policy?.id && (
                              <Dropdown.Item
                                onClick={() => {
                                  setSelectedPolicyIdForDuplicate(String(parseId(policyInfo.policy!.id)))
                                  onOpenDuplicate()
                                }}
                              >
                                <i className='bi bi-collection-fill' /> Duplicate
                              </Dropdown.Item>
                            )}
                            {hsSyncMember && (
                              <>
                                <Dropdown.Divider />
                                {hsSyncMember.healthsherpaContactId ? (
                                  <Dropdown.Item disabled>
                                    <i className='bi bi-check-circle-fill text-success' /> HS Synced
                                  </Dropdown.Item>
                                ) : null}
                                <Dropdown.Item
                                  disabled={hsSearchingMemberId === hsSyncMember.id}
                                  onClick={() => handleSearchBeforeSync(hsSyncMember, node)}
                                >
                                  {hsSearchingMemberId === hsSyncMember.id ? (
                                    <><Spinner animation='border' size='sm' className='me-1' /> Searching HS...</>
                                  ) : hsSyncMember.healthsherpaContactId ? (
                                    <><i className='bi bi-arrow-repeat' /> Re-Sync HealthSherpa</>
                                  ) : (
                                    <><i className='bi bi-arrow-repeat' /> Sync HS</>
                                  )}
                                </Dropdown.Item>
                              </>
                            )}
                            <Dropdown.Divider />
                            <Dropdown.Item
                              onClick={() => {
                                setInfoPreview(node)
                                onOpenPreview()
                              }}
                            >
                              <i className='bi bi-eye' /> Preview
                            </Dropdown.Item>
                          </Dropdown.Menu>
                        </Dropdown>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            )}
          </table>
        </div>
      </div>

      {isOpenFilters && (
        <ModalFilters
          isOpen={isOpenFilters}
          onClose={onCloseFilters}
          onApply={handleFilterApply}
          filters={formData}
          setFilters={setFormData}
          savedFilters={[]}
          allowedImmigrationStatuses={[...MEDICARE_ALLOWED_IMMIGRATION_STATUSES]}
          hideImmigrationStatus
          {...filterData}
        />
      )}

      {isOpenPreview && infoPreview && <ModalPreview idContact={infoPreview.id} onClose={onClosePreview} />}

      <TagManager show={showTagManager} onHide={() => setShowTagManager(false)} onTagsUpdated={refetchMedicareContacts} />

      {tagTargetId && (
        <BulkTagModal
          show={Boolean(tagTargetId)}
          onHide={() => setTagTargetId(null)}
          selectedContactIds={[tagTargetId]}
          onSuccess={() => {
            setTagTargetId(null)
            refetchMedicareContacts()
          }}
        />
      )}

      {isOpenNotes && selectedPolicyIdForNotes && (
        <ModalNotes isOpen={isOpenNotes} onClose={onCloseNotes} idPolicy={selectedPolicyIdForNotes} />
      )}

      {isOpenDuplicate && selectedPolicyIdForDuplicate && (
        <ModalDuplicatePolicy
          id={selectedPolicyIdForDuplicate}
          isOpen={isOpenDuplicate}
          onClose={onCloseDuplicate}
          refetch={refetchMedicareContacts}
        />
      )}

      {isOpenPolicyPreview && selectedPolicyIdForPreview && (
        <PolicyModalPreview
          id={selectedPolicyIdForPreview}
          isOpen={isOpenPolicyPreview}
          onClose={onClosePolicyPreview}
          statusColor={selectedPolicyStatusColor}
        />
      )}

      {isOpenNewPolicy && (
        <ModalAddPolice
          refetch={refetchMedicareContacts}
          isOpen={isOpenNewPolicy}
          onClose={onCloseNewPolicy}
          keyboard={false}
          backdrop='static'
          idContact={selectedContactForPolicy?.id}
          isMedicarePolicyFlow
        />
      )}

      {hsSelectedMember && (
        <ModalHealthSherpaSearch
          isOpen={hsSearchModalOpen}
          onClose={() => setHsSearchModalOpen(false)}
          member={hsSelectedMember}
          searchResults={hsSearchResults}
          onSuccess={handleHsSyncSuccess}
          agentEmail={hsAgentEmail}
        />
      )}
    </div>
  )
}
